import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  User, Lock, Moon, Sun, Globe, Save, Loader2, Eye, EyeOff, ShieldCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const ROLE_LABELS = {
  admin: { label: 'Администратор', color: 'bg-red-500/10 text-red-500 border-red-500/20' },
  manager: { label: 'Менеджер', color: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  worker: { label: 'Работник', color: 'bg-green-500/10 text-green-500 border-green-500/20' },
};

export default function ProfilePage() {
  const { api, user } = useAuth();
  const { language, setLanguage } = useLanguage();
  const [profile, setProfile] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });

  // Theme from localStorage
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    if (user) {
      setProfile({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const applyTheme = (t) => {
    setTheme(t);
    localStorage.setItem('theme', t);
    document.documentElement.classList.toggle('dark', t === 'dark');
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch('/auth/profile', profile);
      toast.success('Профиль обновлён');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка обновления профиля');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('Новые пароли не совпадают');
      return;
    }
    if (passwords.new_password.length < 8) {
      toast.error('Пароль должен быть не менее 8 символов');
      return;
    }
    setSavingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      toast.success('Пароль изменён');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка смены пароля');
    } finally {
      setSavingPassword(false);
    }
  };

  const roleInfo = ROLE_LABELS[user?.role] || ROLE_LABELS.worker;

  const PasswordInput = ({ field, label, showKey }) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <Input
          type={showPasswords[showKey] ? 'text' : 'password'}
          value={passwords[field]}
          onChange={e => setPasswords(prev => ({ ...prev, [field]: e.target.value }))}
          className="pr-10"
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={() => setShowPasswords(prev => ({ ...prev, [showKey]: !prev[showKey] }))}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          {showPasswords[showKey] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 pb-20">
      <div>
        <h1 className="text-2xl font-bold">Профиль</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Управление личными данными и безопасностью</p>
      </div>

      {/* ── Аватар + роль ──────────────────────────── */}
      <Card className="cgc-card">
        <CardContent className="pt-6 pb-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center text-2xl font-bold text-accent">
              {(profile.first_name?.[0] || '?').toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-lg">{profile.first_name} {profile.last_name}</p>
              <p className="text-sm text-muted-foreground">{profile.email}</p>
              <Badge variant="outline" className={cn("mt-1 text-xs", roleInfo.color)}>
                <ShieldCheck className="h-3 w-3 mr-1" />
                {roleInfo.label}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Личные данные ──────────────────────────── */}
      <Card className="cgc-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <User className="h-4 w-4 text-accent" /> Личные данные
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Имя</Label>
              <Input value={profile.first_name} onChange={e => setProfile(p => ({ ...p, first_name: e.target.value }))} placeholder="Иван" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Фамилия</Label>
              <Input value={profile.last_name} onChange={e => setProfile(p => ({ ...p, last_name: e.target.value }))} placeholder="Иванов" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Email</Label>
            <Input type="email" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} placeholder="ivan@example.com" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Телефон</Label>
            <Input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="+1 604 000 0000" />
          </div>
          <Button onClick={handleSaveProfile} disabled={savingProfile} className="w-full">
            {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Сохранить профиль
          </Button>
        </CardContent>
      </Card>

      {/* ── Сменить пароль ─────────────────────────── */}
      <Card className="cgc-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="h-4 w-4 text-accent" /> Безопасность
          </CardTitle>
          <CardDescription>Смена пароля аккаунта</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <PasswordInput field="current_password" label="Текущий пароль" showKey="current" />
          <PasswordInput field="new_password" label="Новый пароль" showKey="new" />
          <PasswordInput field="confirm_password" label="Повторите новый пароль" showKey="confirm" />
          {passwords.new_password && passwords.confirm_password && passwords.new_password !== passwords.confirm_password && (
            <p className="text-xs text-destructive">Пароли не совпадают</p>
          )}
          {passwords.new_password && passwords.new_password.length < 8 && (
            <p className="text-xs text-amber-500">Минимум 8 символов</p>
          )}
          <Button
            onClick={handleChangePassword}
            disabled={savingPassword || !passwords.current_password || !passwords.new_password || passwords.new_password !== passwords.confirm_password}
            variant="outline"
            className="w-full"
          >
            {savingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
            Изменить пароль
          </Button>
        </CardContent>
      </Card>

      {/* ── Внешний вид ────────────────────────────── */}
      <Card className="cgc-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sun className="h-4 w-4 text-accent" /> Внешний вид
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Theme */}
          <div className="space-y-2">
            <Label className="text-xs">Тема</Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'light', label: 'Светлая', icon: Sun },
                { value: 'dark', label: 'Тёмная', icon: Moon },
              ].map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => applyTheme(value)}
                  className={cn(
                    "flex items-center gap-2 p-3 rounded-lg border transition-colors",
                    theme === value ? "bg-accent/10 border-accent text-accent" : "hover:bg-muted/50"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Language */}
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1"><Globe className="h-3 w-3" /> Язык интерфейса</Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'ru', label: 'Русский', flag: '🇷🇺' },
                { value: 'en', label: 'English', flag: '🇨🇦' },
              ].map(({ value, label, flag }) => (
                <button
                  key={value}
                  onClick={() => setLanguage(value)}
                  className={cn(
                    "flex items-center gap-2 p-3 rounded-lg border transition-colors",
                    language === value ? "bg-accent/10 border-accent text-accent" : "hover:bg-muted/50"
                  )}
                >
                  <span>{flag}</span>
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
